// src/components/drawer/AutomationLogs.tsx

import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Search,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputBase,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAccount } from "../../../context";
import {
  AutomationLog,
  LogFilters,
  TemplatePurpose,
} from "../../../interfaces/all";
import { automationApi } from "../../../services/automation";
import Header from "../../chat/Header/Header";

const Container = styled(Box)`
  width: 100%;
  height: 100%;
  background: #fff;
  border-right: 1px solid #e9edef;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  @media (max-width: 600px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }
`;

const ContentContainer = styled(Box)`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 60px);
`;

const TEMPLATE_PURPOSES: { value: TemplatePurpose | "all"; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "convite", label: "Convite" },
  { value: "confirmacao_agendamento", label: "Confirmação de Agendamento" },
  { value: "follow_up", label: "Follow-up" },
];

export const AutomationLogs: React.FC = () => {
  const { account } = useAccount();
  const [logs, setLogs] = useState<AutomationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [purposeFilter, setPurposeFilter] = useState<string>("all");
  const [successFilter, setSuccessFilter] = useState<string>("all");
  const [selectedLog, setSelectedLog] = useState<AutomationLog | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages] = useState(1);
  const [filters] = useState<LogFilters>({
    template_purpose: "all",
    success: undefined,
    limit: 50,
  });

  useEffect(() => {
    if (account?.company_id) {
      loadLogs();
    }
  }, [account?.company_id, filters]);

  // Cleanup adequado para evitar vazamentos de memória
  useEffect(() => {
    return () => {
      // Limpar estados que podem causar vazamentos
      setLogs([]);
      setSelectedLog(null);
    };
  }, []);

  const loadLogs = async () => {
    if (!account?.company_id) return;

    try {
      setLoading(true);
      const response = await automationApi.getLogs(account.company_id, filters);
      if (
        response &&
        "success" in response &&
        response.success &&
        "data" in response
      ) {
        setLogs(response.data);
      } else {
        setError("Erro ao carregar logs");
      }
    } catch (error) {
      setError("Erro ao carregar logs");
      console.error("Erro ao carregar logs:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar logs baseado nos filtros ativos
  const filteredLogs = logs.filter((log) => {
    // Filtro por texto de busca
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      const templateName = log.template_name.toLowerCase();
      const phoneNumber = log.phone_number.toLowerCase();
      const leadId = log.lead_id.toString();

      if (
        !templateName.includes(searchLower) &&
        !phoneNumber.includes(searchLower) &&
        !leadId.includes(searchLower)
      ) {
        return false;
      }
    }

    // Filtro por finalidade
    if (purposeFilter !== "all" && log.template_purpose !== purposeFilter) {
      return false;
    }

    // Filtro por sucesso
    if (successFilter !== "all") {
      const isSuccess = successFilter === "true";
      if (log.success !== isSuccess) {
        return false;
      }
    }

    return true;
  });

  // Função para mostrar detalhes do log
  const handleShowDetails = (log: AutomationLog) => {
    setSelectedLog(log);
    setShowDetails(true);
  };

  // Função para fechar detalhes
  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedLog(null);
  };

  // Função para ir para página anterior
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Função para ir para próxima página
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("pt-BR");
  };

  const getStatusIcon = (success: boolean) => {
    return success ? (
      <CheckIcon color="success" />
    ) : (
      <ErrorIcon color="error" />
    );
  };

  const getPurposeLabel = (purpose: string) => {
    const purposeObj = TEMPLATE_PURPOSES.find((p) => p.value === purpose);
    return purposeObj?.label || purpose;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "INTERESSADO":
        return "primary";
      case "AGENDAMENTO":
        return "success";
      case "CAIXA POSTAL / ENVIO DE MENSAGEM":
        return "warning";
      case "NÃO COMPLETA LIGAÇÃO / ENVIO DE MENSAGEM":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <Container>
      {/* Header igual ao ChatSidebar */}
      <Box sx={{ height: "60px", flexShrink: 0 }}>
        <Header title="Logs de Automação" />
      </Box>

      {/* Conteúdo principal */}
      <ContentContainer>
        {/* Erro */}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Filtros avançados */}
        {!loading && (
          <Box
            sx={{
              p: 2,
              backgroundColor: "#f0f2f5",
              borderBottom: "1px solid #e9edef",
              flexShrink: 0,
            }}
          >
            <Stack spacing={2}>
              {/* Campo de busca */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "4px 8px",
                  border: "1px solid #e9edef",
                  "&:focus-within": {
                    borderColor: "#00a884",
                    boxShadow: "0 0 0 2px rgba(0, 168, 132, 0.1)",
                  },
                }}
              >
                <Search sx={{ color: "#54656f", fontSize: 20, mr: 1 }} />
                <InputBase
                  placeholder="Buscar por template, telefone ou lead..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  sx={{
                    flex: 1,
                    fontSize: "14px",
                    color: "#111b21",
                    "&::placeholder": {
                      color: "#667781",
                    },
                  }}
                />
              </Box>

              {/* Filtros em linha */}
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                {/* Filtro por Finalidade */}
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    value={purposeFilter}
                    onChange={(e) => setPurposeFilter(e.target.value)}
                    displayEmpty
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "6px",
                      fontSize: "13px",
                      width: "100%",
                      "& .MuiSelect-select": {
                        padding: "8px 12px",
                        fontSize: "13px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e9edef",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00a884",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00a884",
                      },
                    }}
                  >
                    <MenuItem value="all">Todas as Finalidades</MenuItem>
                    <MenuItem value="convite">Convite</MenuItem>
                    <MenuItem value="confirmacao_agendamento">
                      Confirmação de Agendamento
                    </MenuItem>
                    <MenuItem value="follow_up">Follow-up</MenuItem>
                  </Select>
                </FormControl>

                {/* Filtro por Status */}
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    value={successFilter}
                    onChange={(e) => setSuccessFilter(e.target.value)}
                    displayEmpty
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "6px",
                      fontSize: "13px",
                      width: "100%",
                      "& .MuiSelect-select": {
                        padding: "8px 12px",
                        fontSize: "13px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e9edef",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00a884",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00a884",
                      },
                    }}
                  >
                    <MenuItem value="all">Todos os Status</MenuItem>
                    <MenuItem value="true">Sucesso</MenuItem>
                    <MenuItem value="false">Erro</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          </Box>
        )}

        {/* Cards de logs - com scroll apenas nos cards */}
        {!loading && (
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              p: 2,
              width: "100%",
              minWidth: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            {filteredLogs.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                  flexDirection: "column",
                  gap: 2,
                  width: "100%",
                  minWidth: "100%",
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  📭 Nenhum log encontrado
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {logs.length === 0
                    ? "Os logs de automação aparecerão aqui quando houver envios"
                    : searchText.trim() ||
                        purposeFilter !== "all" ||
                        successFilter !== "all"
                      ? "Nenhum log encontrado com os filtros aplicados"
                      : "Nenhum log encontrado"}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {filteredLogs.map((log) => (
                  <Paper
                    key={log._id}
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      border: "1px solid #e9edef",
                      borderRadius: "8px",
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                      "&:hover": {
                        backgroundColor: "#f5f6f6",
                        borderColor: "#00a884",
                        boxShadow: "0 2px 8px rgba(0, 168, 132, 0.1)",
                      },
                    }}
                    onClick={() => handleShowDetails(log)}
                  >
                    {/* Header do card */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        {getStatusIcon(log.success)}
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: "#111b21",
                              fontSize: "13px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "200px",
                            }}
                          >
                            {log.template_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#667781",
                              fontSize: "11px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "200px",
                            }}
                          >
                            Lead #{log.lead_id} - {log.phone_number}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        label={log.success ? "Sucesso" : "Erro"}
                        color={log.success ? "success" : "error"}
                        size="small"
                        sx={{ fontSize: "11px", height: "24px" }}
                      />
                    </Box>

                    {/* Informações principais */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        {/* Status do Lead */}
                        <Chip
                          label={log.lead_status}
                          color={getStatusColor(log.lead_status) as any}
                          size="small"
                          sx={{ fontSize: "10px", height: "20px" }}
                        />

                        {/* Finalidade */}
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#667781",
                            fontSize: "10px",
                            fontWeight: 500,
                          }}
                        >
                          {getPurposeLabel(log.template_purpose)}
                        </Typography>
                      </Box>

                      {/* Data */}
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667781",
                          fontSize: "10px",
                          textAlign: "right",
                        }}
                      >
                        {formatDate(log.sent_at)}
                      </Typography>
                    </Box>

                    {/* Footer com informações adicionais */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pt: 1,
                        borderTop: "1px solid #f0f2f5",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667781",
                          fontSize: "10px",
                        }}
                      >
                        WhatsApp ID:{" "}
                        {log.whatsapp_message_id
                          ? log.whatsapp_message_id.substring(0, 20) + "..."
                          : "N/A"}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Paginação igual ao ChatSidebar */}
        {!loading && (
          <Box
            sx={{
              p: 2,
              textAlign: "center",
              borderTop: "1px solid #f0f0f0",
              backgroundColor: "#fff",
              position: "sticky",
              bottom: 0,
              zIndex: 11,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <button
                onClick={goToPreviousPage}
                disabled={loading || currentPage <= 1}
                style={{
                  background:
                    currentPage <= 1 || loading ? "#f5f5f5" : "#00a884",
                  color: currentPage <= 1 || loading ? "#999" : "white",
                  border:
                    currentPage <= 1 || loading ? "1px solid #e0e0e0" : "none",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  cursor:
                    currentPage <= 1 || loading ? "not-allowed" : "pointer",
                  fontSize: "12px",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                  opacity: currentPage <= 1 || loading ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                onMouseOver={(e) => {
                  if (currentPage > 1 && !loading) {
                    e.currentTarget.style.backgroundColor = "#008f6f";
                  }
                }}
                onFocus={(e) => {
                  if (currentPage > 1 && !loading) {
                    e.currentTarget.style.backgroundColor = "#008f6f";
                  }
                }}
                onMouseOut={(e) => {
                  if (currentPage > 1 && !loading) {
                    e.currentTarget.style.backgroundColor = "#00a884";
                  }
                }}
                onBlur={(e) => {
                  if (currentPage > 1 && !loading) {
                    e.currentTarget.style.backgroundColor = "#00a884";
                  }
                }}
              >
                {loading && currentPage > 1 ? (
                  <>
                    <CircularProgress size={12} sx={{ color: "white" }} />
                    Carregando...
                  </>
                ) : (
                  "← Anterior"
                )}
              </button>

              <span
                style={{ fontSize: "12px", color: "#666", padding: "0 8px" }}
              >
                {currentPage} de {totalPages}
              </span>

              <button
                onClick={goToNextPage}
                disabled={loading || currentPage >= totalPages}
                style={{
                  background:
                    currentPage >= totalPages || loading
                      ? "#f5f5f5"
                      : "#00a884",
                  color:
                    currentPage >= totalPages || loading ? "#999" : "white",
                  border:
                    currentPage >= totalPages || loading
                      ? "1px solid #e0e0e0"
                      : "none",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  cursor:
                    currentPage >= totalPages || loading
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "12px",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                  opacity: currentPage >= totalPages || loading ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                onMouseOver={(e) => {
                  if (currentPage < totalPages && !loading) {
                    e.currentTarget.style.backgroundColor = "#008f6f";
                  }
                }}
                onFocus={(e) => {
                  if (currentPage < totalPages && !loading) {
                    e.currentTarget.style.backgroundColor = "#008f6f";
                  }
                }}
                onMouseOut={(e) => {
                  if (currentPage < totalPages && !loading) {
                    e.currentTarget.style.backgroundColor = "#00a884";
                  }
                }}
                onBlur={(e) => {
                  if (currentPage < totalPages && !loading) {
                    e.currentTarget.style.backgroundColor = "#00a884";
                  }
                }}
              >
                {loading && currentPage < totalPages ? (
                  <>
                    <CircularProgress size={12} sx={{ color: "white" }} />
                    Carregando...
                  </>
                ) : (
                  "Próximo →"
                )}
              </button>
            </Box>
          </Box>
        )}
      </ContentContainer>

      {/* Dialog de detalhes - melhorado */}
      <Dialog
        open={showDetails}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            borderBottom: "1px solid #e9edef",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            position: "relative",
          }}
        >
          {/* Elemento decorativo sutil */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 120,
              height: 120,
              background:
                "radial-gradient(circle, rgba(0, 168, 132, 0.05) 0%, transparent 70%)",
              borderRadius: "50%",
              transform: "translate(30px, -30px)",
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              position: "relative",
              zIndex: 1,
            }}
          >
            {selectedLog && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: selectedLog.success ? "#e8f5e8" : "#ffebee",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.8)",
                }}
              >
                {selectedLog && getStatusIcon(selectedLog.success)}
              </Box>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#111b21",
                  mb: 0.5,
                  fontSize: "18px",
                }}
              >
                {selectedLog?.template_name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#667781",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                Lead #{selectedLog?.lead_id} - {selectedLog?.phone_number}
              </Typography>
            </Box>
            {selectedLog && (
              <Chip
                label={selectedLog.success ? "Sucesso" : "Erro"}
                color={selectedLog.success ? "success" : "error"}
                size="small"
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  height: "28px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                }}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedLog && (
            <Box>
              {/* Seção de Informações */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "#111b21",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  ℹ️ Informações do Log
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Lead ID
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {selectedLog.lead_id}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Status do Lead
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {selectedLog.lead_status}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Finalidade
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {getPurposeLabel(selectedLog.template_purpose)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Telefone
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {selectedLog.phone_number}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Enviado em
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "#111b21",
                        mt: 0.5,
                      }}
                    >
                      {formatDate(selectedLog.sent_at)}
                    </Typography>
                  </Box>

                  {selectedLog.whatsapp_message_id && (
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        border: "1px solid #e9edef",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667781",
                          fontSize: "11px",
                          textTransform: "uppercase",
                          fontWeight: 500,
                          letterSpacing: "0.5px",
                        }}
                      >
                        WhatsApp ID
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#111b21",
                          mt: 0.5,
                          fontFamily: "monospace",
                          fontSize: "12px",
                          wordBreak: "break-all",
                        }}
                      >
                        {selectedLog.whatsapp_message_id}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            borderTop: "1px solid #e9edef",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={handleCloseDetails}
            variant="contained"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.2,
              fontSize: "13px",
              backgroundColor: "#00a884",
              boxShadow: "0 2px 8px rgba(0, 168, 132, 0.3)",
              "&:hover": {
                backgroundColor: "#008f6f",
                boxShadow: "0 4px 12px rgba(0, 168, 132, 0.4)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AutomationLogs;
